import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Collapse,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  TableContainer,
} from "@mui/material";
import {
  Add,
  Palette,
  ExpandLess,
  ExpandMore,
  Edit,
  Delete,
} from "@mui/icons-material";
import api from "../../utils/api";

const SchemeManager = () => {
  const [schemes, setSchemes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [form, setForm] = useState({
    id: null,
    color_name: "",
    hex_value: "",
    description: "",
  });
  const [colorForm, setColorForm] = useState({
    type_id: "",
    subtype_id: "",
    color_name: "",
  });
  const [editForm, setEditForm] = useState({
    id: null,
    type_id: "",
    subtype_id: "",
    color_name: "",
  });
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [expandedScheme, setExpandedScheme] = useState(null);
  const [colorOptions, setColorOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchSchemes();
    fetchTypes();
  }, []);

  const fetchSchemes = async () => {
    const res = await api.get("/color-management");
    setSchemes(res.data);
  };

  const fetchTypes = async () => {
    const res = await api.get("/architectural-request-types");
    setTypes(res.data);
  };

  const fetchSubtypes = async (typeId) => {
    if (!typeId) return;
    const res = await api.get(`/architectural-request-subtypes?typeId=${typeId}`);
    setSubtypes(res.data);
  };

  const toggleExpand = async (schemeId) => {
    setExpandedScheme(expandedScheme === schemeId ? null : schemeId);

    if (!colorOptions[schemeId]) {
      setLoadingOptions((prev) => ({ ...prev, [schemeId]: true }));
      try {
        const res = await api.get(`/color-options/by-scheme`, {
          params: { scheme_id: schemeId },
        });
        setColorOptions((prev) => ({ ...prev, [schemeId]: res.data }));
      } catch (err) {
        console.error("Error fetching color options:", err);
      } finally {
        setLoadingOptions((prev) => ({ ...prev, [schemeId]: false }));
      }
    }
  };

  const handleSaveScheme = async () => {
    try {
      if (form.id) {
        await api.put(`/color-management/${form.id}`, form);
      } else {
        await api.post(`/color-management`, form);
      }

      fetchSchemes();
      setDialogOpen(false);
      setSnackbar({
        open: true,
        message: "Scheme saved successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving scheme",
        severity: "error",
      });
    }
  };

  const handleDeleteScheme = async (id) => {
    if (!window.confirm("Delete this scheme?")) return;

    try {
      await api.delete(`/color-management/${id}`);
      fetchSchemes();
      setSnackbar({
        open: true,
        message: "Scheme deleted",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error deleting scheme",
        severity: "error",
      });
    }
  };

  const handleAddColorOption = async () => {
    try {
      if (
        !colorForm.type_id ||
        !colorForm.subtype_id ||
        !colorForm.color_name.trim()
      ) {
        setSnackbar({
          open: true,
          message: "Please select type, subtype, and enter color name",
          severity: "warning",
        });
        return;
      }

      await api.post(`/color-options`, {
        scheme_id: selectedScheme.id,
        type_id: colorForm.type_id,
        subtype_id: colorForm.subtype_id,
        color_name: colorForm.color_name.trim(),
      });

      const res = await api.get(`/color-options/by-scheme`, {
        params: { scheme_id: selectedScheme.id },
      });

      setColorOptions((prev) => ({ ...prev, [selectedScheme.id]: res.data }));
      setColorForm({ type_id: "", subtype_id: "", color_name: "" });
      setColorDialogOpen(false);

      setSnackbar({
        open: true,
        message: "Color option added!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error adding color option:", err);
      setSnackbar({
        open: true,
        message: "Error adding color option",
        severity: "error",
      });
    }
  };

  const handleEditColorOption = async (scheme, opt) => {
    setSelectedScheme(scheme);

    try {
      if (opt.type_id) {
        await fetchSubtypes(opt.type_id);
      }

      setEditForm({
        id: opt.id,
        type_id: opt.type_id || "",
        subtype_id: opt.subtype_id || "",
        color_name: opt.color_name || "",
      });

      setEditDialogOpen(true);
    } catch (err) {
      console.error("Error preparing edit:", err);
    }
  };

  const handleSaveColorEdit = async () => {
    try {
      await api.put(`/color-options/${editForm.id}`, {
        scheme_id: selectedScheme.id,
        type_id: editForm.type_id,
        subtype_id: editForm.subtype_id,
        color_name: editForm.color_name.trim(),
      });

      const res = await api.get(`/color-options/by-scheme`, {
        params: { scheme_id: selectedScheme.id },
      });

      setColorOptions((prev) => ({ ...prev, [selectedScheme.id]: res.data }));
      setEditDialogOpen(false);

      setSnackbar({
        open: true,
        message: "Color option updated!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error updating color option:", err);
      setSnackbar({
        open: true,
        message: "Error updating color option",
        severity: "error",
      });
    }
  };

  const handleDeleteColorOption = async (schemeId, id) => {
    if (!window.confirm("Delete this color option?")) return;

    try {
      await api.delete(`/color-options/${id}`);
      const res = await api.get(`/color-options/by-scheme`, {
        params: { scheme_id: schemeId },
      });
      setColorOptions((prev) => ({ ...prev, [schemeId]: res.data }));
      setSnackbar({
        open: true,
        message: "Color option deleted",
        severity: "success",
      });
    } catch (err) {
      console.error("Error deleting color option:", err);
      setSnackbar({
        open: true,
        message: "Error deleting color option",
        severity: "error",
      });
    }
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  const sectionCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    overflow: "hidden",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Paint (Exterior) Schemes
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Manage paint schemes and assign available color options by type and subtype.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setForm({ id: null, color_name: "", hex_value: "", description: "" });
            setDialogOpen(true);
          }}
          sx={primaryButtonSx}
        >
          Add Scheme
        </Button>
      </Box>

      <Paper elevation={0} sx={sectionCardSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }} />
                <TableCell sx={{ fontWeight: 700 }}>Scheme Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Preview</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {schemes.length > 0 ? (
                schemes.map((s) => (
                  <React.Fragment key={s.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleExpand(s.id)}>
                          {expandedScheme === s.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ fontWeight: 600 }}>{s.color_name}</TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            backgroundColor: s.hex_value || "#ccc",
                            border: "1px solid rgba(0,0,0,0.08)",
                          }}
                        />
                      </TableCell>

                      <TableCell>{s.description || "N/A"}</TableCell>

                      <TableCell align="right">
                        <Button
                          startIcon={<Palette />}
                          variant="outlined"
                          sx={{ ...secondaryButtonSx, mr: 1 }}
                          onClick={() => {
                            setSelectedScheme(s);
                            setColorForm({
                              type_id: "",
                              subtype_id: "",
                              color_name: "",
                            });
                            setSubtypes([]);
                            setColorDialogOpen(true);
                          }}
                        >
                          Add Color Option
                        </Button>

                        <IconButton
                          onClick={() => {
                            setForm({
                              id: s.id,
                              color_name: s.color_name || "",
                              hex_value: s.hex_value || "",
                              description: s.description || "",
                            });
                            setDialogOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>

                        <IconButton color="error" onClick={() => handleDeleteScheme(s.id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          p: 0,
                          borderBottom:
                            expandedScheme === s.id
                              ? "1px solid rgba(47,99,173,0.08)"
                              : "none",
                        }}
                      >
                        <Collapse in={expandedScheme === s.id} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              backgroundColor: "rgba(47,99,173,0.03)",
                              p: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1.5, fontWeight: 700, color: "#223142" }}
                            >
                              Color Options for {s.color_name}
                            </Typography>

                            {loadingOptions[s.id] ? (
                              <Box sx={{ py: 1 }}>
                                <CircularProgress size={22} />
                              </Box>
                            ) : (colorOptions[s.id]?.length || 0) === 0 ? (
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                No color options yet.
                              </Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      Color Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      Subtype
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">
                                      Actions
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {colorOptions[s.id].map((opt) => (
                                    <TableRow key={opt.id}>
                                      <TableCell>{opt.color_name}</TableCell>
                                      <TableCell>{opt.type}</TableCell>
                                      <TableCell>{opt.subtype_name || "N/A"}</TableCell>
                                      <TableCell align="right">
                                        <Button
                                          size="small"
                                          onClick={() => handleEditColorOption(s, opt)}
                                          sx={{ textTransform: "none", fontWeight: 600 }}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleDeleteColorOption(s.id, opt.id)
                                          }
                                          sx={{ textTransform: "none", fontWeight: 600 }}
                                        >
                                          Delete
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: "text.secondary" }}>
                    No schemes found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {form.id ? "Edit Scheme" : "Add New Scheme"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Enter the scheme name, preview color, and optional description.
          </Typography>

          <TextField
            label="Scheme Name"
            fullWidth
            value={form.color_name}
            onChange={(e) => setForm({ ...form, color_name: e.target.value })}
            sx={fieldSx}
          />

          <TextField
            label="Hex Value (#RRGGBB)"
            fullWidth
            value={form.hex_value}
            onChange={(e) => setForm({ ...form, hex_value: e.target.value })}
            sx={fieldSx}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveScheme} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={colorDialogOpen}
        onClose={() => setColorDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Add Color Option
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Add a color option to {selectedScheme?.color_name}.
          </Typography>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Type</InputLabel>
            <Select
              value={colorForm.type_id}
              label="Type"
              onChange={async (e) => {
                const typeId = e.target.value;
                setColorForm({
                  ...colorForm,
                  type_id: typeId,
                  subtype_id: "",
                });
                await fetchSubtypes(typeId);
              }}
            >
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Subtype</InputLabel>
            <Select
              value={colorForm.subtype_id}
              label="Subtype"
              onChange={(e) =>
                setColorForm({ ...colorForm, subtype_id: e.target.value })
              }
            >
              {subtypes.map((s) => (
                <MenuItem key={s.subtype_id} value={s.subtype_id}>
                  {s.subtype_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Color Option Name"
            fullWidth
            value={colorForm.color_name}
            onChange={(e) =>
              setColorForm({ ...colorForm, color_name: e.target.value })
            }
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setColorDialogOpen(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddColorOption} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          Edit Color Option
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Update the type, subtype, and color option name.
          </Typography>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel id="edit-type-label">Type</InputLabel>
            <Select
              labelId="edit-type-label"
              value={editForm.type_id || ""}
              label="Type"
              onChange={async (e) => {
                const newTypeId = e.target.value;
                setEditForm((prev) => ({
                  ...prev,
                  type_id: newTypeId,
                  subtype_id: "",
                }));
                await fetchSubtypes(newTypeId);
              }}
            >
              {types.length === 0 ? (
                <MenuItem disabled>Loading types...</MenuItem>
              ) : (
                types.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.type}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel id="edit-subtype-label">Subtype</InputLabel>
            <Select
              labelId="edit-subtype-label"
              value={editForm.subtype_id || ""}
              label="Subtype"
              onChange={(e) =>
                setEditForm((prev) => ({
                  ...prev,
                  subtype_id: e.target.value,
                }))
              }
            >
              {subtypes.length === 0 ? (
                <MenuItem disabled>
                  {editForm.type_id ? "Loading subtypes..." : "Select a type first"}
                </MenuItem>
              ) : (
                subtypes.map((s) => (
                  <MenuItem key={s.subtype_id} value={s.subtype_id}>
                    {s.subtype_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Color Option Name"
            fullWidth
            value={editForm.color_name || ""}
            onChange={(e) =>
              setEditForm((prev) => ({
                ...prev,
                color_name: e.target.value,
              }))
            }
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button
            onClick={() => setEditDialogOpen(false)}
            variant="outlined"
            sx={secondaryButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveColorEdit}
            disabled={!editForm.color_name?.trim()}
            sx={primaryButtonSx}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchemeManager;
